// React hook that fetches the per-boat alarm state from
// `GET /v1/boats/:boatId/alerts/state` (§5.7) — every ACTIVE definition joined
// with its mirrored runtime state. Shape mirrors cloud/src/api/alerts/routes.ts.
//
// Subscribes to the user-WS `boat.<id>.alerts` topic and refetches whenever the
// boat pushes ANY frame on that lane — an `alert` transition OR an
// `alert_defs_changed` frame (both arrive as `{ type: 'alert', … }`, §8). The
// notification payload is not applied incrementally; we just re-run the GET to
// pick up the reconciled snapshot. Falls back to a passive view if the WS is
// unavailable — the manual `refresh` returned by the hook still works. Cloned
// verbatim from useBoatDevices (same fetch/WS/gap-replay/backoff structure).

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import {
  summarizeAlerts,
  type AlarmSeverity,
  type AlarmStateResponse,
  type BadgeTone,
} from './alarmDisplay';
import { beforeWsReconnect } from './wsReconnect';

export type BoatAlertsState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      response: AlarmStateResponse;
      /** Surfaced rows (runtime present + a surfaced state) — the nav-badge count. */
      activeCount: number;
      /** Most-urgent severity among the surfaced rows (null when none). */
      highestActiveSeverity: AlarmSeverity | null;
      /** Badge tone derived from {@link highestActiveSeverity}. */
      badgeTone: BadgeTone;
    }
  | { kind: 'error'; message: string };

export interface UseBoatAlerts extends Record<string, unknown> {
  state: BoatAlertsState;
  refresh: () => void;
  /**
   * The mode the boat has REPORTED on this subscription — `Anchored`,
   * `Sailing` — or `null` until a `mode_change` frame arrives.
   *
   * Live-only, deliberately. There is no stored "current mode" in the state
   * read, and the alternatives are all guesses: infer it from speed, or
   * remember the last one across a page load and show a mode the boat may have
   * left hours ago. A surface that pauses an alarm's row on this must say
   * nothing at all until the boat says something, so `null` is the honest
   * answer, not a gap to fill.
   */
  currentMode: string | null;
}

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 16_000;

/**
 * Whether a raw user-WS frame on the `boat.<id>.alerts` lane should trigger a
 * re-fetch of the alarm state. Both an `alert` transition and an
 * `alert_defs_changed` frame arrive as `{ type: 'alert', … }` (§8), so a single
 * `type === 'alert'` check covers both; any other lane envelope (or a malformed
 * / non-string frame) is ignored. Pure so the classification is unit-tested
 * without a socket.
 */
export function shouldRefetchForFrame(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  try {
    const frame = JSON.parse(raw) as { type?: unknown };
    return frame.type === 'alert';
  } catch {
    return false;
  }
}

/**
 * The mode id inside a `mode_change` frame, or `null` for every other frame.
 *
 * The lane carries three different things. An `alert_state_update` is
 * published as the boat's RAW payload; `mode_change` and
 * `notification_dispatch` are published as `{ kind, payload }` envelopes
 * (`cloud/src/gateway/boat-ws.ts` `dispatchModeChange`), and the user-WS wraps
 * whichever it is as `{ type: 'alert', topic, ts, payload }`
 * (`cloud/src/gateway/user-ws.ts`). So the mode is at
 * `payload.payload.modeId`, and every level of that is checked — this is wire
 * data from a boat that may be running an older build.
 *
 * Pure, so the frame walk is unit-tested without a socket.
 */
export function modeIdFromFrame(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  let frame: { type?: unknown; payload?: unknown };
  try {
    frame = JSON.parse(raw) as { type?: unknown; payload?: unknown };
  } catch {
    return null;
  }
  if (frame.type !== 'alert') return null;
  const envelope = frame.payload;
  if (envelope === null || typeof envelope !== 'object') return null;
  const { kind, payload } = envelope as { kind?: unknown; payload?: unknown };
  if (kind !== 'mode_change') return null;
  if (payload === null || typeof payload !== 'object') return null;
  const { modeId } = payload as { modeId?: unknown };
  if (typeof modeId !== 'string') return null;
  const trimmed = modeId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function useBoatAlerts(boatId: string): UseBoatAlerts {
  const [state, setState] = useState<BoatAlertsState>({ kind: 'loading' });
  const [currentMode, setCurrentMode] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const lastRequestedBoatId = useRef<string | null>(null);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // GET the alarm state from REST. Re-runs on tick / boatId.
  useEffect(() => {
    if (boatId === '') return;
    let cancelled = false;
    const boatChanged = lastRequestedBoatId.current !== boatId;
    lastRequestedBoatId.current = boatId;
    setState((prev) => (boatChanged || prev.kind !== 'ready' ? { kind: 'loading' } : prev));
    void (async () => {
      try {
        const data = await api<AlarmStateResponse>(`/v1/boats/${boatId}/alerts/state`);
        if (!cancelled) {
          const summary = summarizeAlerts(data);
          setState({
            kind: 'ready',
            response: data,
            activeCount: summary.activeCount,
            highestActiveSeverity: summary.highestActiveSeverity,
            badgeTone: summary.tone,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const e = err as { status?: number; message?: string };
        const message =
          e.status === 403
            ? 'You do not have access to this boat.'
            : e.status === 401
              ? 'Please sign in again.'
              : (e.message ?? 'Failed to load alarms.');
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId, tick]);

  // Neo's confirmation card creates an alarm through the ordinary owner POST
  // (ADR 0161) and announces it on `nm:alarms-updated` — the same window-event
  // pattern the Instruments page uses for `nm:dashboard-updated`. Refetch so an
  // open Alarms page shows the new definition without waiting for the boat to
  // push a defs-changed frame.
  useEffect(() => {
    function onAlarmsUpdated(): void {
      refresh();
    }
    window.addEventListener('nm:alarms-updated', onAlarmsUpdated);
    return () => window.removeEventListener('nm:alarms-updated', onAlarmsUpdated);
  }, [refresh]);

  // Open a user-WS and subscribe to `boat.<id>.alerts`. Every notification just
  // bumps `tick` so the effect above re-runs the GET. Auto-reconnect with
  // bounded backoff. Disabled when there's no boatId yet.
  useEffect(() => {
    // A mode belongs to the boat we are subscribed to. Carrying the last one
    // across a boat switch would label the new boat with the old boat's mode.
    setCurrentMode(null);
    if (boatId === '' || typeof WebSocket === 'undefined') return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = BACKOFF_INITIAL_MS;
    let cancelled = false;

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = backoffMs;
      backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
      reconnectTimer = setTimeout(connect, delay);
    }

    function connect(): void {
      if (cancelled) return;
      const url = new URL('/v1/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffMs = BACKOFF_INITIAL_MS;
        try {
          ws.send(JSON.stringify({ type: 'subscribe', topic: `boat.${boatId}.alerts` }));
          // Re-read the authoritative alarm state now that the subscription is
          // attached: any alert transition / defs change the boat published in
          // the gap between the mount GET and this subscription had no replay, so
          // without this the page can sit on a stale snapshot until a manual
          // reload. Cheap — the GET keeps the current view when already `ready`
          // (no flash) and self-heals on every reconnect. Mirrors useBoatDevices.
          setTick((t) => t + 1);
        } catch {
          // socket gone; onclose will retry
        }
      };
      ws.onmessage = (ev) => {
        // A mode_change frame is BOTH: it says what the boat is doing now, and
        // it changes which definitions are armed, so it also re-reads.
        const mode = modeIdFromFrame(ev.data);
        if (mode !== null) setCurrentMode(mode);
        if (shouldRefetchForFrame(ev.data)) {
          setTick((t) => t + 1);
        }
      };
      ws.onclose = (ev) => {
        socket = null;
        // Renew before retrying when the cloud closed us for an expired access
        // token (task ws_session_expiry): reconnecting with the cookie it just
        // refused only earns another refusal, and the screen would sit stale
        // through the backoff. Reconnects either way.
        void beforeWsReconnect(ev.code).then(scheduleReconnect, scheduleReconnect);
      };
      ws.onerror = () => {
        // onclose handles reconnect.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          /* noop */
        }
        socket = null;
      }
    };
  }, [boatId]);

  return { state, refresh, currentMode };
}
